import { Suspense } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Modal,
  Box,
  Typography,
  Button,
  Stack,
  TextField,
  MenuItem,
  Backdrop,
  CircularProgress,
  Alert,
  Chip,
  TableContainer,
} from "@mui/material";
import { Delete as DeleteIcon, Edit as EditIcon, Add as AddIcon } from "@mui/icons-material";
import { IBaseFormProps } from "./types/types";
import {
  getMatchStatusFilterLabel,
  MATCH_STATUS_FILTER_OPTIONS,
  matchStatusFilterChipSx,
} from "./matchStatusDisplay";
import { MATCH_TABLE_COLUMNS } from "./matchTableDisplay";
import { IGameRecord, IMatch } from "../../types/types";
import { CompactTableColumn } from "./compactTableColumn";
import { useTableList } from "./hooks/useTableList";

interface ITableListProps<T> {
  data: T[];
  title: string;
  headers: Array<string>;
  keys: Array<keyof T>;
  idKey: keyof T;
  FormComponent?: React.ComponentType<IBaseFormProps<T>>;
  gameIdsByMatchId?: Record<string, number[]>;
  gamesByMatchId?: Record<string, IGameRecord[]>;
  compactColumns?: CompactTableColumn<T>[];
  ensureGamesLoaded?: () => void;
  onEdit?: (updatedItem: T) => void | Promise<void>;
  onCreateSubmit?: (newItem: Partial<T>) => void | Promise<void>;
  onDeleteConfirm?: (ids: Array<string>) => void | Promise<void>;
}

const modalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 450,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

const ROLE_OPTIONS = ["TOP", "JUNGLE", "MIDLANE", "BOTTOM", "SUPPORT", "COACH"];

const compactFirstColumnPl = 2;

export function TableList<T extends object>({
  data,
  title,
  headers,
  keys,
  idKey,
  FormComponent,
  gameIdsByMatchId,
  gamesByMatchId,
  compactColumns,
  ensureGamesLoaded,
  onEdit,
  onCreateSubmit,
  onDeleteConfirm,
}: ITableListProps<T>) {
  const {
    availableTeams,
    isMatchTable,
    isCompactTable,
    statusFilter,
    setStatusFilter,
    statusCounts,
    displayData,
    supplementGapsByMatchId,
    handleSort,
    selectedIds,
    handleSelectAllClick,
    handleRowSelectClick,
    openDeleteModal,
    closeDeleteModal,
    handleFinalDelete,
    isDeleteModalOpen,
    itemToDelete,
    deleteError,
    isDeleting,
    openAddModal,
    closeAddModal,
    isAddModalOpen,
    openEditModal,
    closeEditModal,
    isEditModalOpen,
    activeEditingRecord,
    addItemForm,
    editItemForm,
    updateAddItemField,
    updateEditItemField,
    isPendingSave,
    submitError,
    handleFallbackCreateSubmit,
    handleFallbackEditSubmit,
    handleFormCreateSubmit,
    handleFormEditSubmit,
  } = useTableList({
    data,
    keys,
    idKey,
    FormComponent,
    gameIdsByMatchId,
    gamesByMatchId,
    compactColumns,
    ensureGamesLoaded,
    onEdit,
    onCreateSubmit,
    onDeleteConfirm,
  });

  const renderDynamicField = (
    keyName: string,
    value: string,
    onChangeHandler: (val: string) => void,
    isDisabled: boolean = false,
  ) => {
    const normalizedKey = keyName.toLowerCase();

    if (["role", "position", "lane"].includes(normalizedKey)) {
      const normalizedValue = value.toUpperCase();
      const roleExists = ROLE_OPTIONS.includes(normalizedValue);

      return (
        <TextField
          select
          key={keyName}
          label={keyName.toUpperCase()}
          fullWidth
          variant="outlined"
          disabled={isDisabled}
          value={roleExists ? normalizedValue : ""}
          onChange={(e) => {
            onChangeHandler(e.target.value);
          }}
        >
          {!roleExists && value && (
            <MenuItem value={value} disabled>
              {value}
            </MenuItem>
          )}
          {ROLE_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (["team", "teamid", "team_id"].includes(normalizedKey)) {
      const teamExists = availableTeams.some((team) => String(team.id) === String(value));

      return (
        <TextField
          select
          key={keyName}
          label="VALITSE JOUKKUE (TEAM)"
          fullWidth
          variant="outlined"
          disabled={isDisabled}
          value={value || ""}
          onChange={(e) => {
            onChangeHandler(e.target.value);
          }}
        >
          {availableTeams.length === 0 && !value && (
            <MenuItem disabled value="">
              Ei saatavilla olevia joukkueita
            </MenuItem>
          )}

          {value && !teamExists && (
            <MenuItem value={value} disabled>
              Joukkue ID: {value} (Ladataan...)
            </MenuItem>
          )}

          {availableTeams.map((team) => (
            <MenuItem key={team.id} value={team.id}>
              {team.name} (ID: {team.id})
            </MenuItem>
          ))}
        </TextField>
      );
    }

    return (
      <TextField
        key={keyName}
        label={keyName.toUpperCase()}
        fullWidth
        variant="outlined"
        disabled={isDisabled}
        value={value}
        onChange={(e) => {
          onChangeHandler(e.target.value);
        }}
      />
    );
  };

  return (
    <>
      <Backdrop
        open={isPendingSave || isDeleting}
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 2 }}
      >
        <Stack spacing={2} alignItems="center" sx={{ maxWidth: 440, px: 3, textAlign: "center" }}>
          <CircularProgress color="inherit" size={48} />
          <Typography variant="h6" component="p" sx={{ fontWeight: 600 }}>
            Tallennetaan… Älä sulje tai päivitä sivua.
          </Typography>
        </Stack>
      </Backdrop>
      <Box
        sx={{
          p: { xs: "0.75rem", sm: "1rem" },
          width: "100%",
          maxWidth: "100%",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            mb: 4,
            minWidth: isCompactTable ? 0 : "max-content",
            position: "relative",
            minHeight: "50px",
            gap: 2,
          }}
        >
          <Button
            variant="contained"
            color="success"
            startIcon={<AddIcon />}
            onClick={openAddModal}
            sx={{ position: "absolute", left: 0, zIndex: 2 }}
          >
            Lisää uusi kohde
          </Button>

          <Typography
            variant="h5"
            sx={{
              fontWeight: "bold",
              width: "100%",
              textAlign: "center",
              zIndex: 1,
            }}
          >
            {title}
          </Typography>
        </Stack>

        {selectedIds.length > 0 && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ mb: 2, p: 1, bgcolor: "action.hover", borderRadius: 1 }}
          >
            <Typography variant="subtitle1" sx={{ flexGrow: 1, pl: 1 }}>
              {selectedIds.length} kohdetta valittu
            </Typography>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => {
                openDeleteModal(selectedIds);
              }}
            >
              Poista valitut
            </Button>
          </Stack>
        )}

        {isMatchTable && statusCounts && (
          <Stack
            direction="row"
            flexWrap="wrap"
            useFlexGap
            spacing={0.75}
            sx={{ mb: 2, alignItems: "center" }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mr: 0.25, fontSize: "0.8rem" }}
            >
              Tila:
            </Typography>
            {MATCH_STATUS_FILTER_OPTIONS.map((filter) => {
              const selected = statusFilter === filter;

              return (
                <Chip
                  key={filter}
                  label={getMatchStatusFilterLabel(filter, statusCounts[filter])}
                  clickable
                  size="small"
                  color={selected ? "primary" : "default"}
                  variant={selected ? "filled" : "outlined"}
                  sx={matchStatusFilterChipSx(selected)}
                  onClick={() => {
                    setStatusFilter(filter);
                  }}
                  aria-pressed={selected}
                />
              );
            })}
          </Stack>
        )}

        <TableContainer sx={{ width: "100%", maxWidth: "100%" }}>
          <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell
                  align="left"
                  sx={{
                    fontWeight: "bold",
                    width: isCompactTable ? "64px" : "110px",
                    minWidth: isCompactTable ? "64px" : "110px",
                    px: isCompactTable ? 1 : 2,
                  }}
                >
                  {isCompactTable ? "" : "Toiminnot"}
                </TableCell>

                <TableCell
                  padding="checkbox"
                  sx={{
                    width: isCompactTable ? "48px" : "50px",
                    minWidth: isCompactTable ? "48px" : "50px",
                    pl: isCompactTable ? 0.5 : 1,
                    pr: isCompactTable ? 1.5 : 1,
                  }}
                >
                  <Checkbox
                    indeterminate={
                      selectedIds.length > 0 && selectedIds.length < displayData.length
                    }
                    checked={displayData.length > 0 && selectedIds.length === displayData.length}
                    onChange={(e) => {
                      handleSelectAllClick(e.target.checked);
                    }}
                  />
                </TableCell>

                {isMatchTable
                  ? MATCH_TABLE_COLUMNS.map((column, columnIndex) => (
                      <TableCell
                        align={column.align ?? "left"}
                        key={column.id}
                        sx={{
                          cursor: "pointer",
                          fontWeight: "bold",
                          width: column.width,
                          fontSize: "0.78rem",
                          pl: columnIndex === 0 ? compactFirstColumnPl : 0.75,
                          pr: 0.75,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        onClick={() => {
                          handleSort(column.sortKey as keyof T);
                        }}
                      >
                        {column.header}
                      </TableCell>
                    ))
                  : compactColumns
                    ? compactColumns.map((column, columnIndex) => (
                        <TableCell
                          align={column.align ?? "left"}
                          key={column.id}
                          sx={{
                            cursor: "pointer",
                            fontWeight: "bold",
                            width: column.width,
                            fontSize: "0.78rem",
                            pl: columnIndex === 0 ? compactFirstColumnPl : 0.75,
                            pr: 0.75,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          onClick={() => {
                            handleSort(column.sortKey);
                          }}
                        >
                          {column.header}
                        </TableCell>
                      ))
                    : headers.map((h, i) => (
                        <TableCell
                          align="left"
                          key={i}
                          sx={{ cursor: "pointer", fontWeight: "bold" }}
                          onClick={() => {
                            handleSort(keys[i]);
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {displayData.map((entry) => {
                const rowId = String(entry[idKey]);
                const isRowSelected = selectedIds.includes(rowId);

                return (
                  <TableRow key={rowId} hover selected={isRowSelected}>
                    <TableCell
                      align="left"
                      sx={{ px: isCompactTable ? 1 : 2, py: isCompactTable ? 0.75 : 1 }}
                    >
                      <Stack direction="row" spacing={0} alignItems="center">
                        <IconButton
                          aria-label="edit"
                          color="primary"
                          onClick={() => {
                            openEditModal(entry);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          aria-label="delete"
                          color="error"
                          onClick={() => {
                            openDeleteModal([rowId]);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>

                    <TableCell
                      padding="checkbox"
                      sx={{
                        pl: isCompactTable ? 0.5 : 1,
                        pr: isCompactTable ? 1.5 : 1,
                      }}
                    >
                      <Checkbox
                        size={isCompactTable ? "small" : "medium"}
                        checked={isRowSelected}
                        onChange={() => {
                          handleRowSelectClick(rowId);
                        }}
                      />
                    </TableCell>

                    {isMatchTable
                      ? MATCH_TABLE_COLUMNS.map((column, columnIndex) => (
                          <TableCell
                            key={column.id}
                            align={column.align ?? "left"}
                            onClick={() => {
                              openEditModal(entry);
                            }}
                            sx={{
                              cursor: "pointer",
                              pl: columnIndex === 0 ? compactFirstColumnPl : 0.75,
                              pr: 0.75,
                              py: 0.75,
                              fontSize: "0.82rem",
                              overflow: "hidden",
                            }}
                          >
                            {column.render(
                              entry as unknown as IMatch,
                              gameIdsByMatchId?.[rowId] ?? [],
                              supplementGapsByMatchId[rowId],
                            )}
                          </TableCell>
                        ))
                      : compactColumns
                        ? compactColumns.map((column, columnIndex) => (
                            <TableCell
                              key={column.id}
                              align={column.align ?? "left"}
                              onClick={() => {
                                openEditModal(entry);
                              }}
                              sx={{
                                cursor: "pointer",
                                pl: columnIndex === 0 ? compactFirstColumnPl : 0.75,
                                pr: 0.75,
                                py: 0.75,
                                fontSize: "0.82rem",
                                overflow: "hidden",
                              }}
                            >
                              {column.render(entry)}
                            </TableCell>
                          ))
                        : keys.map((k, i) => (
                            <TableCell
                              key={i}
                              onClick={() => {
                                openEditModal(entry);
                              }}
                              sx={{ cursor: "pointer" }}
                            >
                              {String(entry[k])}
                            </TableCell>
                          ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Modal open={isAddModalOpen} onClose={closeAddModal}>
        <Box sx={modalStyle}>
          <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: "bold" }}>
            Lisää uusi kohde
          </Typography>

          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}

          {FormComponent ? (
            <Suspense
              fallback={
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              }
            >
              <FormComponent
                availableTeams={availableTeams}
                onSubmit={handleFormCreateSubmit}
                onCancel={closeAddModal}
              />
            </Suspense>
          ) : (
            <>
              <Stack spacing={2} sx={{ my: 2 }}>
                {keys.map((key) => {
                  if (key === idKey) return null;
                  return renderDynamicField(
                    String(key),
                    addItemForm[String(key)] || "",
                    (newValue) => {
                      updateAddItemField(String(key), newValue);
                    },
                  );
                })}
              </Stack>
              <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                <Button variant="outlined" disabled={isPendingSave} onClick={closeAddModal}>
                  Peruuta
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  disabled={isPendingSave}
                  onClick={() => {
                    void handleFallbackCreateSubmit();
                  }}
                >
                  {isPendingSave ? "Tallennetaan…" : "Tallenna"}
                </Button>
              </Stack>
            </>
          )}
        </Box>
      </Modal>

      <Modal open={isEditModalOpen} onClose={closeEditModal}>
        <Box sx={gameIdsByMatchId ? { ...modalStyle, width: 560 } : modalStyle}>
          <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: "bold" }}>
            Muokkaa tietoja
          </Typography>

          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}

          {FormComponent && activeEditingRecord ? (
            <Suspense
              fallback={
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              }
            >
              <FormComponent
                initialData={activeEditingRecord}
                availableTeams={availableTeams}
                linkedGameIds={gameIdsByMatchId?.[String(activeEditingRecord[idKey])]}
                onSubmit={handleFormEditSubmit}
                onCancel={closeEditModal}
              />
            </Suspense>
          ) : (
            <>
              <Stack spacing={2} sx={{ my: 2 }}>
                {keys.map((key) =>
                  renderDynamicField(
                    String(key),
                    editItemForm[String(key)] || "",
                    (newValue) => {
                      updateEditItemField(String(key), newValue);
                    },
                    key === idKey,
                  ),
                )}
              </Stack>
              <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                <Button variant="outlined" disabled={isPendingSave} onClick={closeEditModal}>
                  Peruuta
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={isPendingSave}
                  onClick={() => {
                    void handleFallbackEditSubmit();
                  }}
                >
                  {isPendingSave ? "Tallennetaan…" : "Päivitä"}
                </Button>
              </Stack>
            </>
          )}
        </Box>
      </Modal>

      <Modal open={isDeleteModalOpen} onClose={closeDeleteModal} disableEscapeKeyDown={isDeleting}>
        <Box sx={modalStyle}>
          <Typography variant="h6" component="h2" gutterBottom>
            Vahvista poisto
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Haluatko varmasti poistaa{" "}
            {itemToDelete.length === 1
              ? "valitun kohteen"
              : `${String(itemToDelete.length)} valittua kohdetta`}
            ? Tätä toimintoa ei voi peruuttaa.
          </Typography>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" disabled={isDeleting} onClick={closeDeleteModal}>
              Peruuta
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={isDeleting}
              onClick={() => {
                void handleFinalDelete();
              }}
            >
              {isDeleting ? "Poistetaan…" : "Poista"}
            </Button>
          </Stack>
        </Box>
      </Modal>
    </>
  );
}
